/**
 * SiteUp: reconhece a mensagem de chegada de lead de anúncio que o bridge
 * despeja no fio como markdown cru.
 *
 * O formato (medido em produção, 3.497 mensagens em 8 contas):
 *
 *   📢 **ANÚNCIO DO FACEBOOK**
 *   **Título:** <título>
 *   **Descrição:**
 *   <descrição, várias linhas>
 *   **Link do Anúncio:** [Ver no Facebook](https://fb.me/xxxx)
 *   **ID do Anúncio:** <dígitos>
 *   ---
 *   💬 **MENSAGEM DO CLIENTE:**
 *   <o que a pessoa realmente escreveu>
 *
 * Média de 765 caracteres, máximo de 1.968 — some com a conversa na tela.
 *
 * INVARIANTE (a bolha ESCONDE o texto original, então isto não é detalhe):
 * só devolve dados quando CADA LINHA do bloco do anúncio foi reconhecida.
 * Qualquer linha que sobre — preâmbulo antes do marcador, texto solto no fim
 * sem o marcador de cliente, campo novo que o bridge passe a mandar — faz o
 * parser devolver `null`, e a mensagem segue como texto comum.
 *
 * A primeira versão só exigia "algum campo preenchido", e o review independente
 * reproduziu três formas de perder a fala do cliente com isso.
 */

// Linha inteira, não substring: uma nota de agente que cite "ANÚNCIO DO
// FACEBOOK" no meio de uma frase não pode virar card e engolir o resto.
const LINE = {
  marker:
    /^[^\p{L}\p{N}]*\**\s*AN[UÚ]NCIO\s+DO\s+FACEBOOK\s*\**[^\p{L}\p{N}]*$/iu,
  title: /^\s*\**\s*T[ií]tulo:?\s*\**(.*)$/i,
  description: /^\s*\**\s*Descri[çc][ãa]o:?\s*\**(.*)$/i,
  link: /^\s*\**\s*Link\s+do\s+An[uú]ncio:?\s*\**(.*)$/i,
  adId: /^\s*\**\s*ID\s+do\s+An[uú]ncio:?\s*\**(.*)$/i,
  separator: /^\s*-{3,}\s*$/,
  blank: /^\s*$/,
};

const CUSTOMER_MARKER =
  /^[^\p{L}\p{N}]*\**\s*MENSAGEM\s+DO\s+CLIENTE:?\s*\**[^\p{L}\p{N}]*$/imu;

const URL_IN_MARKDOWN = /\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/;
const URL_RAW = /(https?:\/\/\S+)/;

const clean = value => (value || '').replace(/\*/g, '').trim();

const extractUrl = value => {
  const md = (value || '').match(URL_IN_MARKDOWN);
  if (md) return md[1];
  const raw = (value || '').match(URL_RAW);
  return raw ? raw[1] : '';
};

/**
 * @param {string} content
 * @returns {boolean} true quando vale tentar o parse completo.
 */
export function isAdArrivalMessage(content) {
  // Early-exit barato: isto roda em TODA mensagem de texto do sistema, então a
  // varredura linha a linha só começa depois de um includes trivial.
  if (typeof content !== 'string' || !content) return false;
  if (!content.includes('FACEBOOK') && !content.includes('Facebook')) {
    return false;
  }
  return content.split('\n').some(line => LINE.marker.test(line));
}

/**
 * Separa o bloco do anúncio da fala do cliente.
 *
 * @param {string} content
 * @returns {{title:string, description:string, link:string, adId:string,
 *   customerMessage:string}|null} `null` sempre que houver qualquer risco de
 *   esconder conteúdo — nesse caso a mensagem deve ser renderizada como texto.
 */
export function parseAdMessage(content) {
  if (!isAdArrivalMessage(content)) return null;

  const parts = content.split(CUSTOMER_MARKER);
  const adBlock = parts[0] || '';
  const customerMessage = parts
    .slice(1)
    .join('\n')
    .replace(/^\s*-{3,}\s*$/gm, '')
    .trim();

  let sawMarker = false;
  let inDescription = false;
  let hasLeftover = false;
  const descriptionLines = [];
  const found = { title: '', link: '', adId: '' };

  adBlock.split('\n').forEach(line => {
    if (LINE.blank.test(line)) {
      if (inDescription) descriptionLines.push('');
      return;
    }
    if (LINE.separator.test(line)) {
      inDescription = false;
      return;
    }
    if (LINE.marker.test(line)) {
      sawMarker = true;
      inDescription = false;
      return;
    }

    const title = line.match(LINE.title);
    if (title) {
      found.title = clean(title[1]);
      inDescription = false;
      return;
    }
    const link = line.match(LINE.link);
    if (link) {
      found.link = extractUrl(link[1]);
      inDescription = false;
      return;
    }
    const adId = line.match(LINE.adId);
    if (adId) {
      found.adId = clean(adId[1]);
      inDescription = false;
      return;
    }
    const description = line.match(LINE.description);
    if (description) {
      inDescription = true;
      if (description[1].trim()) descriptionLines.push(clean(description[1]));
      return;
    }

    // Linha dentro da descrição é conteúdo do anúncio; fora dela é texto que
    // não sabemos posicionar — e esconder o que não se entende é o erro que
    // este parser existe para evitar.
    if (inDescription) {
      descriptionLines.push(line.replace(/\*/g, ''));
      return;
    }
    hasLeftover = true;
  });

  if (!sawMarker || hasLeftover) return null;

  const description = descriptionLines.join('\n').trim();
  const hasAnything =
    found.title || description || found.link || found.adId || customerMessage;
  if (!hasAnything) return null;

  return { ...found, description, customerMessage };
}
